import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  apiFetch,
} from "../api/client";

import {
  tenantPath,
} from "../utils/tenantContext";

import "./TargetCaptureGate.css";


async function requestJson(
  path,
  options = {}
) {
  const response =
    await apiFetch(
      tenantPath(path),
      options
    );

  let body = {};

  try {
    body =
      await response.json();
  } catch (_) {
    // empty body
  }

  if (!response.ok) {
    throw new Error(
      body.detail ||
      `Request failed (${response.status})`
    );
  }

  return body;
}


function TargetCaptureGate() {
  const [devices, setDevices] =
    useState([]);

  const [sensors, setSensors] =
    useState([]);

  const [mappings, setMappings] =
    useState([]);

  const [deviceUuid, setDeviceUuid] =
    useState("");

  const [sensorId, setSensorId] =
    useState("");

  const [duration, setDuration] =
    useState(30);

  const [snaplen, setSnaplen] =
    useState(256);

  const [filter, setFilter] =
    useState("ip or ip6");

  const [preflight, setPreflight] =
    useState(null);

  const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");


  const load =
    useCallback(
      async () => {
        try {
          const [
            d,
            s,
            m,
          ] = await Promise.all([
            requestJson(
              "/network-protection/pcap/targets/devices"
            ),

            requestJson(
              "/network-protection/pcap/targets/sensors"
            ),

            requestJson(
              "/network-protection/pcap/targets/mappings"
            ),
          ]);

          const nextDevices =
            Array.isArray(d.devices)
              ? d.devices
              : [];

          const nextSensors =
            Array.isArray(s.sensors)
              ? s.sensors
              : [];

          setDevices(nextDevices);
          setSensors(nextSensors);

          setMappings(
            Array.isArray(m.mappings)
              ? m.mappings
              : []
          );

          setDeviceUuid(
            (current) =>
              current ||
              nextDevices[0]?.device_uuid ||
              ""
          );

          setSensorId(
            (current) =>
              current ||
              (
                nextSensors[0]?.id
                  ? String(
                      nextSensors[0].id
                    )
                  : ""
              )
          );

        } catch (loadError) {
          setError(
            loadError.message
          );
        }
      },
      []
    );


  useEffect(() => {
    load();
  }, [load]);


  useEffect(() => {
    setPreflight(null);
    setError("");
  }, [
    deviceUuid,
    sensorId,
  ]);


  const mapping =
    useMemo(
      () =>
        mappings.find(
          (item) =>
            item.device_uuid ===
              deviceUuid &&
            String(
              item.sensor_id
            ) === String(
              sensorId
            )
        ) || null,
      [
        mappings,
        deviceUuid,
        sensorId,
      ]
    );


  const canPreflight =
    Boolean(
      mapping &&
      mapping.visibility_status ===
        "verified" &&
      mapping.visibility_method &&
      mapping.visibility_method !==
        "unknown"
    );


  const runPreflight =
    async () => {
      if (!canPreflight) {
        return;
      }

      try {
        setBusy(true);
        setError("");
        setPreflight(null);

        const result =
          await requestJson(
            "/network-protection/pcap/control/target-preflight",
            {
              method: "POST",
              headers: {
                "Content-Type":
                  "application/json",
              },
              body: JSON.stringify({
                sensor_id:
                  Number(sensorId),

                device_uuid:
                  deviceUuid,
              }),
            }
          );

        setPreflight(result);

      } catch (preflightError) {
        setError(
          preflightError.message
        );
      } finally {
        setBusy(false);
      }
    };


  const startTargeted =
    async () => {
      if (
        !preflight?.capture_authorized
      ) {
        return;
      }

      const confirmed =
        window.confirm(
          "Start a bounded packet capture for this VERIFIED target mapping? Only traffic actually visible to the selected sensor can be captured."
        );

      if (!confirmed) return;

      try {
        setBusy(true);
        setError("");

        await requestJson(
          "/network-protection/pcap/control/start-targeted",
          {
            method: "POST",
            headers: {
              "Content-Type":
                "application/json",
            },
            body: JSON.stringify({
              sensor_id:
                Number(sensorId),

              device_uuid:
                deviceUuid,

              duration_seconds:
                Number(duration),

              snaplen:
                Number(snaplen),

              filter_expression:
                filter,
            }),
          }
        );

        setPreflight(null);

      } catch (startError) {
        setError(
          startError.message
        );
      } finally {
        setBusy(false);
      }
    };


  return (
    <section className="targetCaptureGate">
      <div className="targetCaptureGateHeader">
        <div>
          <span>
            TARGETED COLLECTION GATE
          </span>

          <h3>
            Target-Aware Packet Capture
          </h3>

          <p>
            Capture remains blocked until
            this exact sensor/device
            relationship is verified.
          </p>
        </div>

        <div
          className={
            canPreflight
              ? "targetGateBadge ready"
              : "targetGateBadge"
          }
        >
          {canPreflight
            ? "READY FOR PREFLIGHT"
            : "BLOCKED"}
        </div>
      </div>

      {error ? (
        <div className="targetGateError">
          {error}
        </div>
      ) : null}

      <div className="targetGateGrid">
        <label>
          Target Device

          <select
            value={deviceUuid}
            disabled={busy}
            onChange={(event) =>
              setDeviceUuid(
                event.target.value
              )
            }
          >
            {devices.map(
              (device) => (
                <option
                  key={
                    device.device_uuid
                  }
                  value={
                    device.device_uuid
                  }
                >
                  {
                    device.hostname ||
                    device.device_uuid
                  }
                  {" · "}
                  {
                    device.platform ||
                    "unknown"
                  }
                </option>
              )
            )}
          </select>
        </label>

        <label>
          Sensor

          <select
            value={sensorId}
            disabled={busy}
            onChange={(event) =>
              setSensorId(
                event.target.value
              )
            }
          >
            {sensors.map(
              (sensor) => (
                <option
                  key={sensor.id}
                  value={sensor.id}
                >
                  Sensor {sensor.id}
                  {" · "}
                  {
                    sensor.capture_interface ||
                    "unknown"
                  }
                </option>
              )
            )}
          </select>
        </label>

        <label>
          Duration

          <select
            value={duration}
            disabled={busy}
            onChange={(event) =>
              setDuration(
                Number(
                  event.target.value
                )
              )
            }
          >
            <option value={10}>
              10 seconds
            </option>

            <option value={30}>
              30 seconds
            </option>

            <option value={60}>
              60 seconds
            </option>
          </select>
        </label>

        <label>
          Filter

          <select
            value={filter}
            disabled={busy}
            onChange={(event) =>
              setFilter(
                event.target.value
              )
            }
          >
            <option value="ip or ip6">
              All IP
            </option>

            <option value="tcp">
              TCP
            </option>

            <option value="udp">
              UDP
            </option>

            <option value="port 53">
              DNS
            </option>

            <option value="port 80 or port 443">
              Web
            </option>
          </select>
        </label>

        <label>
          Snap Length

          <select
            value={snaplen}
            disabled={busy}
            onChange={(event) =>
              setSnaplen(
                Number(
                  event.target.value
                )
              )
            }
          >
            <option value={96}>
              96
            </option>

            <option value={128}>
              128
            </option>

            <option value={256}>
              256
            </option>

            <option value={512}>
              512
            </option>
          </select>
        </label>
      </div>

      <div className="targetGateMapping">
        <div>
          <span>
            Visibility
          </span>

          <strong>
            {
              mapping
                ?.visibility_status
                ?.replaceAll(
                  "_",
                  " "
                )
                .toUpperCase()
              ||
              "NO MAPPING"
            }
          </strong>
        </div>

        <div>
          <span>
            Method
          </span>

          <strong>
            {
              mapping
                ?.visibility_method
                ?.toUpperCase()
              ||
              "—"
            }
          </strong>
        </div>

        <div>
          <span>
            Verified By
          </span>

          <strong>
            {
              mapping
                ?.verified_by ||
              "—"
            }
          </strong>
        </div>

        <div>
          <span>
            Verified At
          </span>

          <strong>
            {
              mapping
                ?.verified_at
                ? new Date(
                    mapping.verified_at
                  ).toLocaleString()
                : "—"
            }
          </strong>
        </div>
      </div>

      <div className="targetGateActions">
        <button
          type="button"
          disabled={
            busy ||
            !canPreflight
          }
          onClick={runPreflight}
        >
          Run Capture Preflight
        </button>

        <button
          type="button"
          className="targetGateStart"
          disabled={
            busy ||
            !preflight
              ?.capture_authorized
          }
          onClick={startTargeted}
        >
          Start Targeted Capture
        </button>
      </div>

      {preflight
        ?.capture_authorized ? (
          <div className="targetGateAuthorized">
            Preflight passed.
            Visibility is VERIFIED.
            Target capture is authorized
            for this sensor/device mapping.
          </div>
        ) : null}

      <div className="targetGateBoundary">
        VERIFIED mapping is mandatory.
        A mapping records the expected
        visibility path; it does not
        create that path. The sensor can
        only capture traffic physically
        delivered to its interface.
      </div>
    </section>
  );
}

export default TargetCaptureGate;
